import { CalendarContainer } from './styled';
import SpecificDay from './SpecificDay';
import { configurationActions } from 'core/Configuration';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import {
    ButtonFooter,
    Card,
    Container,
    DateSelect,
    ExpandButton,
    FormGroup,
    IconButton,
    Input,
    Loader,
    Modal,
    TabController,
    Text
} from 'mol-ui';
import {
    Calendar,
    NotificationModal
} from 'views/components';
import { Col, Row } from 'reactstrap';
import React, { useEffect, useState } from 'react';
import { dateToObject, objectToDate } from 'shared/utils';
import {
    useArray,
    useDispatch,
    useInput,
    useSelector,
    useToggle,
    useWindowSize
} from 'hooks';

const MethodByHour = () => {

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { configurations: { absence_rate_method } } = useSelector('configuration');
    const {
        absenceRate,
        absenceRate: { special_days = [] },
        absenceRateLoading,
        absenceRateProcessing: processing,
        absenceRateUpserted,
        scheduleCreation,
        scheduleCreationError,
        scheduleUpdateError,
        schedules: schedulesList
    } = useSelector('configuration');
    const schedules = absence_rate_method === 'by_hour_schedule';
    const { xs } = useWindowSize();
    const [active, toggleActive] = useToggle(false);
    const [date, setDate] = useState(dateToObject(moment()));
    const [employeesPerWorkPlace, setEmployeesPerWorkplace, {
        replaceValues: replaceEmployees
    }] = useArray([], { type: 'object' });
    const [hoursPerWeek, setHoursPerWeek, {
        replaceValues: replaceHoursPerDay
    }] = useArray([], { type: 'object' });
    const [newScheduleInput, newSchedule, setNewSchedule] = useInput('', {}, () => {
        !!scheduleCreationError && dispatch(configurationActions.setConfigurationScheduleCreationError(''));
    });
    const [scheduleInput, schedule, setSchedule] = useInput('', {}, () => {
        !!scheduleUpdateError && dispatch(configurationActions.setConfigurationScheduleUpdateError(''));
    });
    const [selected, setSelected] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);
    const [workplaceInput, workplaceFilter] = useInput('');

    const parseHighlightDays = special_days.map(({ date }) => moment(date).date());

    useEffect(() => {
        const data = {
            month: Number(date?.month),
            ...((!!selected && schedules) && { schedule_id: selected }),
            year: Number(date?.year)
        };
        !!schedules && dispatch(configurationActions.getAbsenceRateSchedules(data));
        dispatch(configurationActions.getAbsenceRateConfiguration(data));
    }, [date, selected]);

    useEffect(() => {
        if (!scheduleCreation) return;
        toggleActive();
        dispatch(configurationActions.setConfigurationScheduleCreation(false));
        setNewSchedule('');
    }, [scheduleCreation]);

    useEffect(() => {
        if (isEmpty(absenceRate)) return;
        const { daily_hours, workplaces } = absenceRate ?? {};
        setEmployeesPerWorkplace(workplaces);
        setHoursPerWeek(daily_hours);
    }, [absenceRate]);

    useEffect(() => {
        if (!isEmpty(schedulesList) && !selected) {
            setSelected(schedulesList[0].key);
            setSchedule(schedulesList[0].label);
        }
    }, [selected, schedulesList]);

    const handleCopyWorkplaceEmployees = () => {
        setEmployeesPerWorkplace(currentWorkplaces => currentWorkplaces.map(workplace => ({
            ...workplace,
            employees: workplace?.workplace_employees ?? workplace?.employees
        })));
    };

    const handleDaySelect = value => {
        setDate(value);
        setSelectedDate(null);
    };

    const handleDaySearch = day => {
        const data = {
            day: day,
            month: date?.month,
            year: date?.year
        };
        setSelectedDate(objectToDate(data));
    };

    const handleChangeScheduleSelected = id => {
        setSelected(id);
        setSchedule(schedulesList.find(({ key }) => key === id)?.label);
    };
        
    const onSubmit = () => {
        const scheduleUpdated = {
            id: selected,
            name: schedule
        };
        const payload = {
            days: hoursPerWeek,
            month: Number(date?.month),
            scheduleUpdated,
            workplaces: employeesPerWorkPlace,
            year: Number(date?.year)
        };
        if (!schedules) dispatch(configurationActions.upsertAbsenceRateConfiguration(payload));
        else dispatch(configurationActions.upsertAbsenceRateScheduleConfig(payload));
    };

    const onCancelNewSchedule = () => {
        toggleActive();
        setNewSchedule('');
        !!scheduleCreationError && dispatch(configurationActions.setConfigurationScheduleCreationError(''));
    };

    const onSubmitNewSchedule = () => {
        dispatch(configurationActions.upsertAbsenceRateSchedule({ name: newSchedule }));
    };
    
    const title = t('Régimen laboral');

    const filteredEmployeesPerWorkPlace = employeesPerWorkPlace.filter(({ name }) => !workplaceFilter || name.toLowerCase().indexOf(workplaceFilter.toLowerCase()) >= 0);
    
    return (
        <Container>
            {
                schedules && !isEmpty(schedulesList) &&
                <Row>
                    <TabController
                        borderless
                        items={ schedulesList }
                        margin="10px"
                        value={ selected }
                        onChange={ handleChangeScheduleSelected }
                    />
                </Row>
            }
            <Modal active={ active } onToggle={ toggleActive }>
                <FormGroup label={ title }>
                    <Input
                        { ...newScheduleInput }
                        error={ scheduleCreationError }
                        hasError={ !!scheduleCreationError }
                    />
                </FormGroup>
                <ButtonFooter margin="20px 0px">
                    <ExpandButton
                        danger
                        caption={ t('Cancelar') }
                        size={ 100 }
                        onClick={ onCancelNewSchedule }
                    />
                    <ExpandButton
                        caption={ t('Confirmar') }
                        loading={ absenceRateLoading }
                        size={ 100 }
                        onClick={ onSubmitNewSchedule }
                    />
                </ButtonFooter>

            </Modal>
            <NotificationModal
                singleButton
                active={ absenceRateUpserted }
                height="auto"
                title={ t('Configuración actualizada correctamente') }
                onBack={ () => dispatch(configurationActions.setConfigurationAbsenceRateUpserted(false)) }
            />
            {
                schedules &&
                <Row>
                    <Col sm={ 1 } xs={ 4 }>
                        <ExpandButton
                            caption={ t('Agregar régimen') }
                            icon="add"
                            margin="0px 0px 20px 0px"
                            size={ 80 }
                            width={ 150 }
                            onClick={ toggleActive }
                        />
                    </Col>
                </Row>
            }
            <Row>
                <Col sm={ 6 } xs={ 12 }>
                    <FormGroup label={ t('Mes y año') }>
                        <DateSelect
                            hideDays={ true }
                            margin="0px 0px 20px 0px"
                            nextYears={ 1 }
                            value={ date }
                            onChange={ handleDaySelect }
                        />
                    </FormGroup>
                </Col>
                {
                    schedules && !isEmpty(schedulesList) &&
                    <Col sm={ 3 } xs={ 8 }>
                        <FormGroup label={ title }>
                            <Input
                                { ...scheduleInput }
                                error={ scheduleUpdateError }
                                hasError={ !!scheduleUpdateError }
                            />
                        </FormGroup>
                    </Col>
                }
            </Row>
            <Row>
                {
                    absenceRateLoading
                        ? <Loader active />
                        : <>
                            <Col xs="12">
                                <FormGroup label={ t('Días de la semana') }>
                                    {
                                        hoursPerWeek.map(days => {
                                            const { day, label, working_hours } = days;
                                            const text = `${ label }:`;
                                            return <Container alignItems="center" flexDirection="row" key={ day } margin="5px 0px 0px 0px">
                                                <Text margin="0px 10px 0px 0px">
                                                    { text }
                                                </Text>
                                                <Input
                                                    flex="1"
                                                    height="20px"
                                                    margin="0px"
                                                    type="number"
                                                    value={ (working_hours ?? 0).toString() }
                                                    width={ xs ? '100%' : '100px' }
                                                    onChange={ value => replaceHoursPerDay({ working_hours: value }, 'day', day) }
                                                />
                                            </Container>;
                                        })
                                    }
                                </FormGroup>
                            </Col>
                            <FormGroup label={ `${ t('Buscar lugar de trabajo') }...` } margin="20px 0px 0px 0px">
                                <Input { ...workplaceInput } />
                            </FormGroup>
                            <IconButton
                                icon="copy_all"
                                label={ t('Utilizar valores actuales') }
                                margin="0px 0px 10px 0px"
                                size={ 16 }
                                onClick={ handleCopyWorkplaceEmployees }
                            />
                            <Card margin="0px 0px 20px 0px" maxHeight="400px" overflowY="auto" title={ t('Lugares de trabajo y cantidad de empleados') } width="100%">
                                {
                                    filteredEmployeesPerWorkPlace.map(places => {
                                        const { employees, name, workplace_id, workplace_employees } = places;
                                        return (
                                            <Container alignItems="center" flexDirection="row" key={ workplace_id } margin="5px 0px 0px 0px">
                                                <Text margin="0px 5px 0px 0px">
                                                    { name }
                                                </Text>
                                                {
                                                    !!workplace_employees &&
                                                    <Text fontSize="10px" margin="0px 5px 0px 0px">
                                                        ({ t('Actuales') }: { workplace_employees })
                                                    </Text>
                                                }
                                                <Input
                                                    flex="1"
                                                    height="20px"
                                                    margin="0px"
                                                    type="number"
                                                    value={ (employees ?? 0).toString() }
                                                    width={ xs ? '100%' : '100px' }
                                                    onChange={ value => replaceEmployees({ employees: value }, 'workplace_id', workplace_id) }
                                                />
                                            </Container>
                                        );
                                    })
                                }
                            </Card>
                            <ButtonFooter margin="20px 0px">
                                <ExpandButton
                                    caption={ t('Confirmar') }
                                    disabled={ schedules && !selected }
                                    loading={ processing }
                                    size={ 100 }
                                    onClick={ onSubmit }
                                />
                            </ButtonFooter>
                            <CalendarContainer>
                                <Col sm="4" xs="12">
                                    <Calendar
                                        highlightDays={ parseHighlightDays }
                                        month={ Number(date?.month) }
                                        title={ t('Calendario') }
                                        year={ Number(date?.year) }
                                        onDaySelect={ handleDaySearch }
                                    />
                                </Col>
                                {
                                    selectedDate &&
                                    <Col sm="4" xs="12">
                                        <SpecificDay
                                            clearDate={ setSelectedDate }
                                            specificDate={ selectedDate }
                                        />
                                    </Col>
                                }
                            </CalendarContainer>
                        </>
                }
            </Row>
        </Container>
    );
};

MethodByHour.propTypes = {

};

export { MethodByHour };
